import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { StoryOverview, Project } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { CoverArt } from "../library/CoverArt.js";
import { statusLabel } from "../lib/format.js";

interface Props { projectId: string; reloadKey?: number; }

const ELEMENT_ICONS: IconName[] = ["volume", "home", "timeline", "sparkles"];

/**
 * Book Overview hero — cover, AI-generated whole-book summary, themes, and the
 * recurring motifs ("Key Elements"), built from chapter summaries (Book Cache).
 * Read-only; refreshes when the cache is rebuilt.
 */
export function BookOverviewCard({ projectId, reloadKey }: Props) {
  const [overview, setOverview] = useState<StoryOverview | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.storyOverview(projectId).then((r) => r.overview).catch(() => null),
      api.dashboard(projectId).then((d) => d.project).catch(() => null)
    ]).then(([ov, pr]) => {
      if (cancelled) return;
      setOverview(ov); setProject(pr); setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [projectId, reloadKey]);

  if (!loaded) return null;

  const summary = overview?.summary || project?.description || "";
  const themes = (overview?.themes ?? "").split(/[,;]/).map((t) => t.trim()).filter(Boolean);
  const motifs = overview?.motifs ?? [];

  return (
    <section className="book-hero">
      <div className="book-hero-glow" />
      <div className="book-hero-cover">
        {project && <CoverArt project={{ title: project.title, author: project.author, coverImage: project.coverImage }} />}
      </div>

      <div className="book-hero-main">
        <div className="row gap-2" style={{ alignItems: "center", marginBottom: 8 }}>
          <h3 className="book-hero-title">Book Overview</h3>
          {project && <span className="chip chip-accent"><Icon name="edit" size={11} /> {statusLabel(project.status)}</span>}
        </div>
        {summary ? (
          <p className="book-hero-summary">{summary}</p>
        ) : (
          <p className="faint" style={{ fontSize: 13, margin: 0 }}>
            Not generated yet. Open <strong>Book Cache</strong> and build it — Scribe summarizes each chapter, then writes a whole-book overview.
          </p>
        )}
        {themes.length > 0 && (
          <div className="book-hero-themes">
            <div className="book-hero-themes-label">Themes</div>
            <div className="row gap-2" style={{ flexWrap: "wrap" }}>
              {themes.map((th, i) => <span key={i} className={`chip ${i % 2 === 0 ? "chip-accent" : "chip-purple"}`}>{th}</span>)}
            </div>
          </div>
        )}
      </div>

      <div className="book-hero-elements">
        <div className="book-hero-elements-label">Key Elements</div>
        {motifs.length === 0 ? (
          <p className="faint" style={{ fontSize: 12, margin: 0 }}>Recurring motifs appear here once the book overview is built.</p>
        ) : (
          motifs.slice(0, 4).map((m, i) => (
            <div key={i} className="book-element">
              <span className="book-element-ico"><Icon name={ELEMENT_ICONS[i % ELEMENT_ICONS.length]!} size={16} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="book-element-name">{m.name}</div>
                {m.description && <div className="book-element-desc">{m.description}</div>}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
